<script setup lang="ts">
import { computed } from "vue";

interface BlogPost {
  slug: string;
  title: string;
  author?: string;
  publishedAt?: string;
  excerpt?: string;
  image?: string;
  readTime?: string;
  html: string;
}

const route = useRoute();
const slug = computed(() => String(route.params.slug || ""));

const { data: post, error } = await useFetch<BlogPost>(
  () => `/api/blog/${slug.value}`,
  { watch: [slug] },
);

if (error.value || !post.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Post not found",
    fatal: true,
  });
}

const canonical = computed(() => `https://rozay.today/blog/${post.value?.slug}/`);
const seoImage = computed(() => {
  const image = post.value?.image;
  if (!image) return "https://rozay.today/blog/ramadan-2026-guide.svg";
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  return `https://rozay.today${image.startsWith("/") ? image : `/${image}`}`;
});

const blogPostingSchema = computed(() => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.value?.title ?? "",
  description: post.value?.excerpt ?? "",
  datePublished: post.value?.publishedAt ?? "",
  dateModified: post.value?.publishedAt ?? "",
  author: {
    "@type": "Organization",
    name: post.value?.author ?? "Rozay.today Team",
  },
  publisher: {
    "@type": "Organization",
    name: "Rozay.today",
    logo: {
      "@type": "ImageObject",
      url: "https://rozay.today/favicon-32.png",
    },
  },
  image: [seoImage.value],
  mainEntityOfPage: canonical.value,
}));

useSeoMeta({
  title: () => post.value?.title ?? "Blog",
  description: () => post.value?.excerpt ?? "",
  ogTitle: () => post.value?.title ?? "Blog",
  ogDescription: () => post.value?.excerpt ?? "",
  ogUrl: () => canonical.value,
  ogType: "article",
  ogImage: () => seoImage.value,
  twitterCard: "summary_large_image",
  twitterImage: () => seoImage.value,
});

useHead(() => ({
  link: [{ rel: "canonical", href: canonical.value }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(blogPostingSchema.value),
    },
  ],
}));
</script>

<template>
  <article
    v-if="post"
    class="max-w-3xl mx-auto px-4 py-8"
    :aria-labelledby="`post-title-${post.slug}`"
  >
    <NuxtLink
      to="/blog/"
      class="text-sm text-emerald-700 dark:text-emerald-300 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 rounded-sm"
    >
      Back to Blog
    </NuxtLink>

    <h1 :id="`post-title-${post.slug}`" class="mt-3 text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
      {{ post.title }}
    </h1>
    <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
      {{ post.publishedAt }} | {{ post.author }}
      <span v-if="post.readTime"> | {{ post.readTime }}</span>
    </p>

    <img
      v-if="post.image"
      :src="post.image"
      :alt="post.title"
      class="mt-5 w-full rounded-xl border border-gray-200 dark:border-gray-800"
      loading="lazy"
    />

    <div class="blog-content mt-6 text-gray-800 dark:text-gray-200">
      <div v-html="post.html"></div>
    </div>
  </article>
</template>

<style scoped>
.blog-content :deep(h1),
.blog-content :deep(h2),
.blog-content :deep(h3) {
  font-weight: 700;
  line-height: 1.3;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.blog-content :deep(h1) {
  font-size: 1.75rem;
}

.blog-content :deep(h2) {
  font-size: 1.35rem;
}

.blog-content :deep(h3) {
  font-size: 1.15rem;
}

.blog-content :deep(p),
.blog-content :deep(ul),
.blog-content :deep(ol) {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  line-height: 1.8;
}

.blog-content :deep(ul),
.blog-content :deep(ol) {
  padding-left: 1.25rem;
}

.blog-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  display: block;
  overflow-x: auto;
}

.blog-content :deep(th),
.blog-content :deep(td) {
  border: 1px solid #d1d5db;
  padding: 0.6rem 0.75rem;
  text-align: left;
  vertical-align: top;
}

.blog-content :deep(th) {
  background: #f3f4f6;
  font-weight: 700;
}

.dark .blog-content :deep(th),
.dark .blog-content :deep(td) {
  border-color: #374151;
}

.dark .blog-content :deep(th) {
  background: #1f2937;
}

.blog-content :deep(a) {
  color: #047857;
  text-decoration: underline;
}

.blog-content :deep(a:focus-visible) {
  outline: 2px solid #047857;
  outline-offset: 2px;
  border-radius: 2px;
}

.blog-content :deep(strong) {
  font-weight: 700;
}
</style>
